/**
 * TPG Player — Embed playback page (Sprint 8 Phase C).
 *
 * Commercial embed surface for partners. Loads Media Asset via public embed API,
 * runs through the same control plane as HostPlaybackPage (no JW).
 * Parent pages may listen for postMessage events (tpg-player:*).
 */
import React, { Component } from "react";
import api from "../../Environment";
import NextGenPlayer from "../Components/NextGenPlayer";
import { PlayerEvent } from "../Core/PlayerEvents";
import {
  buildHostPlayerConfig,
  siteFeatures,
} from "./buildHostPlayerConfig";
import { resolveHostControlPlane } from "./resolveHostControlPlane";
import { createSubtitlesPlugin } from "../Plugins/builtins/subtitlesPlugin";
import { createHostAnalyticsPlugin } from "../Plugins/builtins/hostAnalyticsPlugin";
import { createAvodPlugin } from "../Plugins/builtins/avodPlugin";
import "../Themes/player.css";
import "./EmbedPlaybackPage.css";

function postToParent(type, payload) {
  try {
    if (typeof window !== "undefined" && window.parent && window.parent !== window) {
      window.parent.postMessage(
        { source: "tpg-player", type: type, payload: payload || {} },
        "*"
      );
    }
  } catch (e) {
    /* ignore */
  }
}

class EmbedPlaybackPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      error: null,
      details: null,
      controlPlane: null,
    };
    this.handleReady = this.handleReady.bind(this);
    this.handleEnded = this.handleEnded.bind(this);
    this.handleError = this.handleError.bind(this);
    this.handlePlaybackEvent = this.handlePlaybackEvent.bind(this);
  }

  componentDidMount() {
    this._load();
  }

  componentDidUpdate(prevProps) {
    var prevId =
      (prevProps.match && prevProps.match.params && prevProps.match.params.uniqueId) ||
      "";
    var nextId =
      (this.props.match && this.props.match.params && this.props.match.params.uniqueId) ||
      "";
    if (prevId !== nextId) this._load();
  }

  _uniqueId() {
    return (
      (this.props.match && this.props.match.params && this.props.match.params.uniqueId) ||
      ""
    );
  }

  _vType() {
    var q =
      this.props.location && this.props.location.search
        ? this.props.location.search
        : "";
    var m = /[?&]v_t=(\d+)/.exec(q);
    return m ? Number(m[1]) : 2;
  }

  _load() {
    var self = this;
    var uniqueId = this._uniqueId();
    if (!uniqueId) {
      this.setState({ loading: false, error: "Missing embed id" });
      return;
    }
    this.setState({ loading: true, error: null });
    api
      .postMethod("embed/view", {
        u_id: uniqueId,
        v_t: this._vType(),
      })
      .then(function (response) {
        if (!response.data || !response.data.success) {
          self.setState({
            loading: false,
            error:
              (response.data && response.data.error_messages) ||
              "Embed media unavailable",
          });
          postToParent("error", { message: "unavailable" });
          return;
        }
        var details = response.data.data;
        var plane = resolveHostControlPlane(details, {
          siteFeatures: Object.assign({}, siteFeatures(), {
            casting: false,
            theaterMode: false,
            fullscreenOnFirstPlay: false,
            avod: !!details.avod_enabled,
          }),
        });
        self.setState({
          loading: false,
          details: details,
          controlPlane: plane,
        });
        postToParent("loaded", {
          contentId: details.admin_video_id,
          presentationKind:
            plane.mediaAsset && plane.mediaAsset.presentationKind,
        });
      })
      .catch(function () {
        self.setState({ loading: false, error: "Embed request failed" });
        postToParent("error", { message: "request_failed" });
      });
  }

  handleReady(info) {
    postToParent("ready", {
      provider: info && info.provider,
    });
  }

  handleEnded() {
    postToParent("ended", {});
  }

  handleError(err) {
    postToParent("error", {
      message: (err && err.message) || "playback_error",
    });
  }

  handlePlaybackEvent(name) {
    if (name === PlayerEvent.PLAYBACK_STARTED || name === "play") {
      postToParent("play", {});
    }
    if (name === PlayerEvent.PLAYBACK_PAUSED || name === "pause") {
      postToParent("pause", {});
    }
  }

  _buildConfig() {
    var details = this.state.details;
    var plane = this.state.controlPlane;
    var plugins = [
      createSubtitlesPlugin(),
      createHostAnalyticsPlugin({
        continuousSession: false,
        sink: function (evt) {
          postToParent("analytics", evt);
        },
      }),
    ];
    if (details && details.ad_cues && details.ad_cues.length) {
      plugins.push(
        createAvodPlugin({
          cues: details.ad_cues,
        })
      );
    }
    return buildHostPlayerConfig(details, {
      controlPlane: plane,
      plugins: plugins,
      autoplay: true,
      applySeek: false,
      features: {
        casting: false,
        theaterMode: false,
        continueWatching: false,
        avod: !!(details && details.ad_cues && details.ad_cues.length),
      },
    });
  }

  render() {
    if (this.state.loading) {
      return (
        <div className="tpg-embed-page tpg-embed-page--loading">
          Loading…
        </div>
      );
    }
    if (this.state.error || !this.state.details) {
      return (
        <div className="tpg-embed-page tpg-embed-page--error" role="alert">
          {this.state.error || "Unavailable"}
        </div>
      );
    }
    var config = this._buildConfig();
    return (
      <div className="tpg-embed-page">
        <NextGenPlayer
          config={config}
          onReady={this.handleReady}
          onEnded={this.handleEnded}
          onError={this.handleError}
          onPlaybackEvent={this.handlePlaybackEvent}
        />
      </div>
    );
  }
}

export default EmbedPlaybackPage;
